import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, Paper, Stack, Chip, CircularProgress, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Switch,
} from '@mui/material';
import { Add, Delete, Edit, Flag, PhotoCamera, Event as EventIcon, LocationOn, People } from '@mui/icons-material';
import { format } from 'date-fns';
import { eventService, DiveEvent, EventRsvpStatus, UpdateEventRequest } from '../services/eventService';
import { userProfileService } from '../services/userProfileService';
import { authService } from '../services/authService';
import { useNotification } from './NotificationContext';
import ReportDialog from './ReportDialog';

function eventPhotoSrc(photoKey?: string | null): string | undefined {
  return photoKey ? `https://aidivecoach-post-photos.s3.us-east-2.amazonaws.com/${photoKey}` : undefined;
}

type EventFields = { title: string; description?: string; photoKey?: string; startTime: string; endTime?: string; location?: string; isPublic?: boolean };

/** An ISO instant as the local "yyyy-MM-ddTHH:mm" a datetime-local input wants. */
const toLocalInput = (iso?: string | null): string => (iso ? format(new Date(iso), "yyyy-MM-dd'T'HH:mm") : '');

// Reuses the same presigned-upload flow profile/group/club photos already use — same
// public bucket, same allowlist, no event-specific upload endpoint needed. Creates a new
// event, or edits `event` when one is passed.
function EventDialog({
  open, onClose, onSubmit, event, regionName, joinNotice,
}: {
  open: boolean;
  onClose: () => void;
  /** When set, the dialog edits this event instead of creating one. */
  event?: DiveEvent | null;
  /** Set when hosting for a region community: hides the public-home-page switch (region events are already open to every signed-in diver). */
  regionName?: string;
  /** True when hosting will also add the host to the community. */
  joinNotice?: boolean;
  onSubmit: (request: EventFields) => Promise<void>;
}) {
  const { showError } = useNotification();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [photoKey, setPhotoKey] = useState<string | undefined>(undefined);
  const [isPublic, setIsPublic] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  // Fill from the event being edited, or blank for a new one, each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setTitle(event?.title ?? '');
    setDescription(event?.description ?? '');
    setLocation(event?.location ?? '');
    setStartTime(toLocalInput(event?.startTime));
    setEndTime(toLocalInput(event?.endTime));
    setPhotoKey(event?.photoKey ?? undefined);
    setIsPublic(event?.isPublic ?? false);
  }, [open, event]);

  const handlePhotoSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      showError('Please choose a JPEG, PNG, or WEBP image');
      return;
    }
    setUploadingPhoto(true);
    try {
      setPhotoKey(await userProfileService.uploadPhoto(file));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!title.trim() || !startTime) return;
    setSubmitting(true);
    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim() || undefined,
        photoKey,
        startTime: new Date(startTime).toISOString(),
        endTime: endTime ? new Date(endTime).toISOString() : undefined,
        location: location.trim() || undefined,
        isPublic,
      });
      onClose();
    } catch {
      // The caller already told the user what went wrong; stay open so their edits aren't lost.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{event ? 'Edit Event' : 'New Event'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar variant="rounded" src={eventPhotoSrc(photoKey)} sx={{ width: 64, height: 64, bgcolor: 'primary.main' }}>
              <EventIcon />
            </Avatar>
            <Button
              size="small"
              variant="outlined"
              startIcon={uploadingPhoto ? <CircularProgress size={14} /> : <PhotoCamera fontSize="small" />}
              onClick={() => photoInputRef.current?.click()}
              disabled={uploadingPhoto}
            >
              {photoKey ? 'Change cover photo' : 'Add cover photo'}
            </Button>
            <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={handlePhotoSelected} />
          </Box>
          <TextField label="Title" value={title} onChange={e => setTitle(e.target.value)} fullWidth autoFocus />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={e => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField label="Location (optional)" value={location} onChange={e => setLocation(e.target.value)} fullWidth />
          {regionName && (
            <Typography variant="caption" color="text.secondary">
              This event will be visible to every diver who opens the {regionName} community.
              {joinNotice && !event ? ' Hosting also adds you to the community.' : ''}
            </Typography>
          )}
          {!regionName && <FormControlLabel
            control={<Switch checked={isPublic} onChange={e => setIsPublic(e.target.checked)} />}
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Show on the public home page</Typography>
                <Typography variant="caption" color="text.secondary">
                  Lets people who aren't signed in see the title, time, place and how many are going. Off by default.
                </Typography>
              </Box>
            }
          />}
          <TextField
            label="Starts"
            type="datetime-local"
            value={startTime}
            onChange={e => setStartTime(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Ends (optional)"
            type="datetime-local"
            value={endTime}
            onChange={e => setEndTime(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !title.trim() || !startTime}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          {event ? 'Save changes' : 'Create Event'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Events tab body for a Group or Club home page — a hostable meetup/social with an RSVP
 * feed, distinct from a ScheduledDive's invite/join list. Any active member may host one,
 * same precedent CreateScheduledDive already sets for open-invite dives.
 */
interface EventsPanelProps {
  groupId?: string;
  clubId?: string;
  /** Hosts/lists events for a region community instead of a group or club. */
  regionSlug?: string;
  regionName?: string;
  /** Called before a region event is created so the host is a member (the server requires it). Return false to cancel. */
  ensureRegionMember?: () => Promise<boolean>;
  /** True when hosting will newly add the viewer to the community — shown in the create dialog. */
  willJoinOnHost?: boolean;
  currentUserId: string | null;
}

const EventsPanel: React.FC<EventsPanelProps> = ({ groupId, clubId, regionSlug, regionName, ensureRegionMember, willJoinOnHost, currentUserId }) => {
  // Admins may remove region events (open to strangers); anyone may remove their own event.
  const isAdmin = !!authService.getUser()?.isAdmin;
  const { showSuccess, showError } = useNotification();
  const [events, setEvents] = useState<DiveEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<DiveEvent | null>(null);
  const [reporting, setReporting] = useState<DiveEvent | null>(null);
  const [rsvpingId, setRsvpingId] = useState<string | null>(null);
  const [rsvpStatus, setRsvpStatus] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = regionSlug
        ? await eventService.getEventsForRegion(regionSlug)
        : groupId
          ? await eventService.getEventsForGroup(groupId)
          : clubId
            ? await eventService.getEventsForClub(clubId)
            : [];
      setEvents(list);

      if (currentUserId) {
        const statuses = await Promise.all(list.map(async ev => {
          try {
            const rsvps = await eventService.getRsvps(ev.eventId);
            return [ev.eventId, rsvps.some(r => r.userId === currentUserId)] as const;
          } catch {
            return [ev.eventId, false] as const;
          }
        }));
        setRsvpStatus(Object.fromEntries(statuses));
      }
    } catch {
      // silently swallow — an empty Events tab is a reasonable fallback
    } finally {
      setLoading(false);
    }
  }, [groupId, clubId, regionSlug, currentUserId]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (request: { title: string; description?: string; photoKey?: string; startTime: string; endTime?: string; location?: string; isPublic?: boolean }) => {
    try {
      if (regionSlug && ensureRegionMember && !(await ensureRegionMember())) return;
      const ev = await eventService.createEvent({ ...request, groupId, clubId, regionSlug });
      setEvents(prev => [...prev, ev].sort((a, b) => a.startTime.localeCompare(b.startTime)));
      showSuccess('Event created');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create event');
    }
  };

  const handleEdit = async (request: EventFields) => {
    if (!editing) return;
    const changes: UpdateEventRequest = {
      title: request.title,
      description: request.description ?? '',
      photoKey: request.photoKey ?? '',
      location: request.location ?? '',
    };
    // Only send times the host actually changed (the form works to the minute): the server rejects a
    // start in the past, which would otherwise block fixing a typo on an event that has already begun.
    if (toLocalInput(request.startTime) !== toLocalInput(editing.startTime)) changes.startTime = request.startTime;
    if (request.endTime && toLocalInput(request.endTime) !== toLocalInput(editing.endTime)) changes.endTime = request.endTime;
    try {
      const updated = await eventService.updateEvent(editing.eventId, changes);
      setEvents(prev => prev.map(e => (e.eventId === updated.eventId ? { ...e, ...updated } : e)).sort((a, b) => a.startTime.localeCompare(b.startTime)));
      showSuccess('Event updated');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update event');
      throw err; // keep the dialog open so the edits aren't lost
    }
  };

  const handleDelete = async (eventId: string) => {
    if (!window.confirm('Delete this event? Everyone who RSVPed loses it.')) return;
    try {
      await eventService.deleteEvent(eventId);
      setEvents(prev => prev.filter(e => e.eventId !== eventId));
      showSuccess('Event deleted');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete event');
    }
  };

  const handleRsvp = async (eventId: string) => {
    setRsvpingId(eventId);
    try {
      await eventService.rsvp(eventId, EventRsvpStatus.Going);
      setEvents(prev => prev.map(e => (e.eventId === eventId ? { ...e, goingCount: e.goingCount + (rsvpStatus[eventId] ? 0 : 1) } : e)));
      setRsvpStatus(prev => ({ ...prev, [eventId]: true }));
      showSuccess("You're going");
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to RSVP');
    } finally {
      setRsvpingId(null);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={20} /></Box>;
  }

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" size="small" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
          Host an Event
        </Button>
      </Box>

      {events.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">{regionName ? `No upcoming events in ${regionName} — host one for the community.` : 'No events yet — host one for the group to join.'}</Typography>
        </Box>
      )}

      {events.map(ev => {
        const isHost = ev.hostUserId === currentUserId;
        const isGoing = !!rsvpStatus[ev.eventId];
        return (
          <Paper key={ev.eventId} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Avatar variant="rounded" src={eventPhotoSrc(ev.photoKey)} sx={{ width: 56, height: 56, bgcolor: 'primary.main', flexShrink: 0 }}>
                <EventIcon />
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{ev.title}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {format(new Date(ev.startTime), 'MMM d, yyyy · h:mm a')} · Hosted by {ev.hostDisplayName}
                </Typography>
                {ev.location && (
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                    <LocationOn sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">{ev.location}</Typography>
                  </Stack>
                )}
                {ev.description && (
                  <Typography variant="body2" sx={{ mt: 1, overflowWrap: 'anywhere' }}>{ev.description}</Typography>
                )}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                  <Chip
                    icon={<People sx={{ fontSize: 14 }} />}
                    label={regionSlug ? `${ev.goingCount} going` : `${ev.goingCount} ${ev.goingCount === 1 ? 'buddy' : 'buddies'} going`}
                    size="small"
                    variant="outlined"
                  />
                  {isHost && (
                    <Button size="small" startIcon={<Edit />} onClick={() => setEditing(ev)}>Edit</Button>
                  )}
                  {!isHost && !!ev.regionSlug && !!currentUserId && (
                    <Button size="small" color="inherit" startIcon={<Flag />} onClick={() => setReporting(ev)}>Report</Button>
                  )}
                  {(isHost || (isAdmin && !!ev.regionSlug)) && (
                    <Button size="small" color="error" startIcon={<Delete />} onClick={() => handleDelete(ev.eventId)}>
                      {isHost ? 'Delete' : 'Remove (admin)'}
                    </Button>
                  )}
                  {isHost ? (
                    <Chip label="You're hosting" size="small" color="primary" />
                  ) : isGoing ? (
                    <Chip label="You're going" size="small" color="primary" />
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleRsvp(ev.eventId)}
                      disabled={rsvpingId === ev.eventId}
                      startIcon={rsvpingId === ev.eventId ? <CircularProgress size={14} /> : undefined}
                    >
                      I'm going
                    </Button>
                  )}
                </Stack>
              </Box>
            </Stack>
          </Paper>
        );
      })}

      <EventDialog open={createOpen} onClose={() => setCreateOpen(false)} onSubmit={handleCreate} regionName={regionName} joinNotice={willJoinOnHost} />
      <EventDialog open={!!editing} event={editing} onClose={() => setEditing(null)} onSubmit={handleEdit} regionName={editing?.regionSlug ? regionName : undefined} />
      {reporting && (
        <ReportDialog
          target={{ type: 'Event', eventId: reporting.eventId }}
          offender={{ userId: reporting.hostUserId, displayName: reporting.hostDisplayName }}
          onClose={() => setReporting(null)}
          onBlocked={userId => setEvents(prev => prev.filter(e => e.hostUserId !== userId))}
        />
      )}
    </Stack>
  );
};

export default EventsPanel;
